import { Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { IPricingProfilePartPricing } from 'pricing/types';
import { Level } from 'theme/enums';

import { derivePurchaseCost, deriveStandardCost, getCostSpread, toNumericValue } from './helpers';

interface IProps {
  companyPricing?: IPricingProfilePartPricing;
  standardCostMultiplier?: number;
}

export const UpsertPricingProfilePartNumberCostFields = ({
  companyPricing,
  standardCostMultiplier,
}: IProps) => {
  const { t } = useTranslation();

  const { watch, setValue, control, errors } = useFormContext();
  const costDecimals = useCostDecimals();

  const purchasedCostValue = watch('purchasedCost');
  const costValue = watch('cost');

  const companyPurchaseCost = companyPricing?.purchasedCost;
  const companyStandardCost = companyPricing?.cost;

  // Auto-fill runs on blur, not on change: the legacy modal listens to jQuery's `change`, which
  // fires once the input is committed — per-keystroke would fill from a half-typed cost.
  const onBlurPurchasedCost = () => {
    const derivedStandardCost = deriveStandardCost(
      purchasedCostValue,
      standardCostMultiplier,
      costDecimals
    );

    if (null === derivedStandardCost || toNumericValue(costValue) > 0) {
      return;
    }

    setValue('cost', derivedStandardCost);
  };

  const onBlurCost = () => {
    const derivedPurchaseCost = derivePurchaseCost(costValue, standardCostMultiplier, costDecimals);

    if (null === derivedPurchaseCost || toNumericValue(purchasedCostValue) > 0) {
      return;
    }

    setValue('purchasedCost', derivedPurchaseCost);
  };

  const costInputProps = {
    control,
    slotProps: {
      input: {
        prefix: '$',
        thousandSeparator: true,
        decimalScale: costDecimals,
        fixedDecimalScale: true,
      },
    },
  };

  return (
    // gap matches the 16px rhythm Form's ContentWrapper gives its own direct children.
    <Stack gap={2}>
      <FormField
        name="purchasedCost"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          ...costInputProps,
          error: errors?.purchasedCost,
          label: t('labelPurchaseCost'),
          placeholder: t('labelPurchaseCost'),
          onBlur: onBlurPurchasedCost,
          helperTexts: [
            t('labelCompanyPurchaseCostWithValue', {
              value: PricingHelper.formatPricing({ amount: companyPurchaseCost }),
            }),
          ],
        }}
      />

      <FormField
        name="cost"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          ...costInputProps,
          error: errors?.cost,
          label: t('labelStandardCost'),
          placeholder: t('labelStandardCost'),
          onBlur: onBlurCost,
          endDecorator: (
            <Typography level={Level.BODY_XS}>
              {getPercentage(getCostSpread(purchasedCostValue, costValue))}
            </Typography>
          ),
          helperTexts: [
            t('labelCompanyStandardCostWithValue', {
              value: PricingHelper.formatPricing({ amount: companyStandardCost }),
              spread: getPercentage(getCostSpread(companyPurchaseCost, companyStandardCost)),
            }),
          ],
        }}
      />
    </Stack>
  );
};
